/**
 * SEO Component
 * 
 * Dynamic meta tags and structured data management using react-helmet-async
 * Optimized for search engines and social media sharing
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  keywords?: string[];
  author?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Productive Capabilities Data Lab',
  description = 'The Productive Capabilities Data Lab is an applied AI research and development hub specializing in analytics and datasets for industrial strategy: It is a non-profit, public-purpose collaboration between i3T and RubidiumData providing detailed value chain and supply chain analysis with interactive tools.',
  canonical = 'https://valuechains.ai/',
  ogType = 'website',
  ogImage = 'https://valuechains.ai/i3t-logo.png',
  twitterCard = 'summary_large_image',
  keywords = [
    'value chains',
    'HS codes',
    'trade data',
    'industrial policy',
    'supply chain analytics',
    'productive capabilities',
    'company mapping',
    'technology mapping',
    'economic complexity',
    'AI research',
  ],
  author = 'i3T & RubidiumData',
  jsonLd,
  noindex = false
}) => {
  const fullTitle = title === 'Productive Capabilities Data Lab' 
    ? title 
    : `${title} | Productive Capabilities Data Lab`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="author" content={author} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph Tags (Facebook, LinkedIn) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Productive Capabilities Data Lab" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Value Chains" />
      
      {/* Language */}
      <html lang="en" />
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

/**
 * Predefined SEO configurations for common pages
 */
export const SEOConfigs = {
  home: {
    title: 'Productive Capabilities Data Lab',
    description: 'Analyze and visualize technology value chains, HS codes, and productive capabilities beyond trade data. Explore industrial policy, trade data, and supply chain analytics.',
    canonical: 'https://valuechains.ai/',
    keywords: ['value chains', 'HS codes', 'trade data', 'industrial policy', 'supply chain analytics']
  },
  
  tradeCodes: {
    title: 'Trade Codes Analysis',
    description: 'Explore detailed HS code mappings and trade data for technology value chains. Analyze product classifications and international trade statistics.',
    canonical: 'https://valuechains.ai/trade-codes',
    keywords: ['HS codes', 'trade codes', 'product classification', 'harmonized system', 'trade statistics', 'value chains']
  },
  
  technologyTypes: {
    title: 'Technology Types Analysis',
    description: 'Discover technology types across value chains underlying our HS code mappings.',
    canonical: 'https://valuechains.ai/technology-types',
    keywords: ['technology types', 'industrial technology', 'product analysis', 'value chains', 'trade codes']
  },
  
  systems: {
    title: 'Systems Analysis',
    description: 'Explore complete value chain systems and their interconnections. Analyze end-to-end supply chains and productive capabilities.',
    canonical: 'https://valuechains.ai/systems',
    keywords: ['value chain systems', 'supply chain analysis', 'technology breakdown', 'product value chain', 'process value chain', 'production systems', 'trade codes']
  },
  
  companies: {
    title: 'Companies Analysis',
    description: 'Discover companies across technology value chains. Analyze company locations, capabilities, and value chain participation.',
    canonical: 'https://valuechains.ai/companies',
    keywords: ['companies', 'company analysis', 'business intelligence', 'value chain participants']
  },
  
  about: {
    title: 'About Us',
    description: 'Learn about the Productive Capabilities Data Lab, our mission, and the organizations behind this innovative platform.',
    canonical: 'https://valuechains.ai/about',
    keywords: ['about', 'mission', 'i3T', 'RubidiumData', 'industrial policy design']
  },
  
  auth: {
    title: 'Sign in',
    description: 'Sign in to access your Productive Capabilities Data Lab account.',
    canonical: 'https://valuechains.ai/auth',
    noindex: true // Don't index auth pages
  }
};

/**
 * Generate structured data for the organization
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'i3T & RubidiumData',
  url: 'https://valuechains.ai/',
  logo: 'https://valuechains.ai/i3t-logo.png',
  description: 'Leading research organization providing data analytics for industrial strategy and value chain analysis.',
  sameAs: [
    // Add social media profiles here if available
  ]
});

/**
 * Generate structured data for a web application
 */
export const getWebApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Productive Capabilities Data Lab',
  url: 'https://valuechains.ai/',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Any',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD'
  },
  description: 'The Productive Capabilities Data Lab breaks down technology value chains, maps products to HS codes, and links them to producing companies at the product level going beyond the limits of trade-data classifications. It is a beta tool for applied industrial policy research and development.',
  screenshot: 'https://valuechains.ai/visualization-dashboard.gif'
});

/**
 * Generate structured data for a dataset
 */
export const getDatasetSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'Technology Value Chains and HS Codes Dataset',
  description: 'Comprehensive dataset mapping technology value chains to harmonized system codes and producing companies across countries.',
  url: 'https://valuechains.ai/',
  keywords: ['value chains', 'HS codes', 'trade data', 'industrial policy'],
  creator: {
    '@type': 'Organization',
    name: 'i3T & RubidiumData',
    url: 'https://valuechains.ai/'
  },
  license: 'https://valuechains.ai/about',
  temporalCoverage: '2020/..',
  spatialCoverage: {
    '@type': 'Place',
    name: 'Global'
  }
});

/**
 * Generate breadcrumb structured data
 */
export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

